/**
 * taxBlocks.js — custom Blockly block definitions for the Tax Calculate editor (S1-P7).
 *
 * PURE ESM. Imports Blockly ONLY through the cross-env shim `./blocklyCore.js` (NEVER
 * `blockly/core` directly — under Node that yields `Blockly.Workspace === undefined`).
 * NO top-level DOM access: the module registers block definitions on import via the shim's
 * `Blockly`, so the headless `.mjs` unit tests can `ws.newBlock(type)` immediately.
 *
 * ── What this module owns ─────────────────────────────────────────────────────────
 * The block MODEL only (fields, inputs, connections, serialization). It does NOT write the
 * NCalc generator (task 3-A), the toolbox (2-C) or the theme (2-B). Colours come from `style`
 * category keys resolved by the theme — no hex is hard-coded here.
 *
 * ── Serialization contract (for 3-A generator + the v2 workspace blob) ───────────────
 *   tax_start     flow-entry hat. NO output, NO previous — only nextStatement. NON-compiling
 *                 (marks where the pipeline begins). Defines chain below it in eval order.
 *   tax_define    statement block. field_input OUTPUTVAR (auto-serialized). value input EXPR.
 *                 previous/next statement so defines stack START → def → def → … → FINAL.
 *                 extraState: { appliesToKind, appliesToKey, evalOrder, notes }.
 *                 → 3-A: valueToCode(EXPR) is the formula RHS; metadata from getTaxMeta().
 *   tax_result    flow-exit cap. previousStatement only. field_input RESULTVAR (auto-serialized,
 *                 the final output var the engine consumes, e.g. net_income). NON-compiling.
 *   tax_formula_result  the per-formula COMPILE ANCHOR. value input VALUE, no output/previous/next
 *                 (a true root). Whatever is plugged into VALUE is the formula; every other top-level
 *                 block on the canvas is scratch and is NOT compiled. Distinct from tax_result: that
 *                 one caps the retired whole-version chain and names a variable, this one holds an
 *                 expression. No fields → no extraState, and it never appears in a pre-existing
 *                 ExpressionGraphJson (see ncalcGenerator's pre-result-block compat path).
 *   tax_variable  value. field_label_serializable NAME — FIXED text, NOT typeable: the block IS the
 *                 identifier it came from the flyout with ("Formula expense_40_5"). A typeable NAME was
 *                 the only way to author an identifier that does not exist in the version; the flyout
 *                 only ever offers names that do, so a different name = a different block. Still
 *                 auto-serialized (SERIALIZABLE=true) → the blob shape `fields:{NAME}` is UNCHANGED from
 *                 the field_input it replaces, so every stored graph loads and re-saves byte-identically.
 *                 extraState: { source, flagged } OUT; { source, flagged, desc? } IN — `desc` is a derived
 *                 gloss the toolbox seeds and the view re-derives; it is never saved (field_label RULECTX
 *                 is non-serializable). Same for field_label FLAG, the trailing "!" derived from
 *                 `flagged` (setTaxFlagged) — DERIVED, never a persisted key.
 *                 → 3-A: emit NAME. source is display-only (drives style/label), never compiled.
 *   tax_func_sum  value. single value input VALUE. NO fields, NO extraState — it is a pure bracket
 *                 around an iterated computation. → 3-A: SUM(<VALUE>), arity EXACTLY 1, never nested,
 *                 and its argument must contain a `[]` path (there is otherwise nothing to sum).
 *                 (It REPLACES the retired `tax_sum_items` Σ: a path iterates and sums by itself, so
 *                 what was left to express was only WHERE the sum happens — see formulaGraph SUM_FN.)
 *   tax_number    value. field_number VALUE.                → 3-A: emit the literal.
 *   tax_text      value(String). field_input TEXT — the ONE tax_* block whose text IS typed by hand:
 *                 a literal is authored, not chosen (tax_variable's NAME is a fixed label because a
 *                 name must exist in the catalog; 'FEMALE' exists nowhere but in the author's head).
 *                 The field validator holds it to the shared literal grammar (formulaGraph's
 *                 sanitizeStringLiteral), so a quote/backslash cannot be typed in at all.
 *                 → 3-A: emit 'TEXT' (single-quoted). Output "String" — comparison operand ONLY.
 *   tax_arith     value. field_dropdown OP ∈ {ADD,SUB,MUL,DIV}. inputs A,B (check: Number|Boolean).
 *                 → 3-A: A <sym> B  (sym: + - * /).
 *   tax_compare   value(Boolean). field_dropdown OP ∈ {LT,LE,GT,GE,EQ,NE}. inputs A,B (UNCHECKED).
 *                 → 3-A: A <sym> B  (sym: < <= > >= == !=).
 *   tax_func_min  value. VARIADIC (mutator, ≥2 inputs ADD0..ADDn). extraState: { itemCount }.
 *   tax_func_max  value. VARIADIC (mutator, ≥2 inputs ADD0..ADDn). extraState: { itemCount }.
 *                 → 3-A: MIN(a, b, …) / MAX(a, b, …) — flat, never nested.
 *   tax_func_round value. input X (required) + input N (optional 2nd).
 *                 → 3-A: N empty → ROUND(x); N connected → ROUND(x, n).
 *   tax_func_if   value. fixed inputs COND, THEN, ELSE.      → 3-A: IF(cond, then, else).
 *   tax_constant  value. field_input CONSTKEY + field_number CONSTVALUE (both auto-serialized).
 *                 → 3-A: emit CONSTKEY (value NEVER inlined); collectConstants reads key+value.
 *   tax_bracket   root block. NON-compiling. custom field_bracket_button EDIT.
 *                 extraState: { rows:[{lowerBound,upperBound,rate,sortOrder}] }; getBracketRows().
 *
 * Op/compare codes + func arities mirror the retiring `utils/formulaGraph.js` exactly so 3-A can
 * reproduce byte-identical `Expression` strings (AC4 zero-drift).
 */
import Blockly from './blocklyCore.js'
import { sanitizeStringLiteral, MAX_STRING_LITERAL_LEN, pathDisplay } from '../formulaGraph.js'

// ── Style category keys (colours supplied by the theme, task 2-B) ─────────────────
export const TAX_BLOCK_STYLE_KEYS = [
  'tax_rule_style', // tax_variable RuleInput / RuleFields (the latter = the off-catalog stale bucket)
  'tax_rate_style', // tax_variable RuleRate — a rate is NOT an amount, so it never shares rule blue
  'tax_personal_style', // tax_variable PersonalInfo
  'tax_global_style', // tax_variable Globals
  'tax_const_style', // tax_constant
  'tax_output_style', // tax_define + tax_variable OutputVar
  'tax_number_style', // tax_number
  'tax_text_style', // tax_text — a literal like tax_number, but NOT a number: its own hue or the
  //                   canvas would claim 'FEMALE' plugs anywhere a 0 does
  'tax_item_style', // tax_func_sum — the Σ that reduces an iterated computation to one number. It
  //                   keeps the hue the retired per-item aggregate wore, because it is still the one
  //                   block on the canvas that says "this is about ROWS, not about one value"

  'tax_path_style', // tax_path — a NESTED path (INCOME_40_5 ▸ subItems[] ▸ amount). Its OWN key, not
  //                   tax_rule_style: a `[]` path is not one value, it is one value PER ITEM, and a
  //                   block that looks identical to a scalar rule variable hides exactly that
  'tax_bool_style', // tax_bool — the TRUE/FALSE literal (IFS's always-matching last branch)
  'tax_op_style', // tax_arith
  'tax_compare_style', // tax_compare
  'tax_func_style', // tax_func_min / max / round / if (+ mutator helper blocks)
  'tax_bracket_style', // tax_bracket
  'tax_start_style', // tax_start (flow entry / hat)
  'tax_result_style', // tax_result (flow exit / final output) + tax_formula_result (per-formula anchor)
]

// The per-formula compile anchor. Exported so the Formula editor and its tests name it from one
// place: it is the only tax_* block a toolbox must NEVER offer (the view mints exactly one and makes
// it non-deletable), so a stray toolbox entry is the drift worth guarding against.
export const TAX_FORMULA_RESULT_TYPE = 'tax_formula_result'

// ── Primary block type strings (consumed by the toolbox, task 2-C) ────────────────
export const TAX_BLOCK_TYPES = [
  'tax_start',
  'tax_define',
  'tax_result',
  TAX_FORMULA_RESULT_TYPE,
  'tax_variable',
  'tax_path',
  'tax_number',
  'tax_text',
  'tax_arith',
  'tax_compare',
  'tax_func_min',
  'tax_func_max',
  'tax_func_round',
  'tax_func_if',
  'tax_func_ifs',
  'tax_func_sum',
  'tax_bool',
  'tax_constant',
  'tax_bracket',
]

// Internal helper blocks used only inside the variadic mutator flyout (NOT toolbox blocks).
export const TAX_MUTATOR_BLOCK_TYPES = ['tax_func_container', 'tax_func_item', 'tax_ifs_container', 'tax_ifs_item']

// ── Operator / comparator dropdown options (code ⇄ display symbol) ─────────────────
// Values are the SAME codes formulaGraph.js uses (OP_SYM / CMP_SYM keys) → 3-A maps 1:1.
const OP_OPTIONS = [
  ['+', 'ADD'],
  ['−', 'SUB'], // U+2212 MINUS SIGN (display); compiles to '-'
  ['×', 'MUL'], // U+00D7 MULTIPLICATION SIGN; compiles to '*'
  ['÷', 'DIV'], // U+00F7 DIVISION SIGN; compiles to '/'
]
const CMP_OPTIONS = [
  ['<', 'LT'],
  ['<=', 'LE'],
  ['>', 'GT'],
  ['>=', 'GE'],
  ['==', 'EQ'],
  ['!=', 'NE'],
]

// ── tax_variable source → { style, label } (source is display-only, never compiled) ──
const SOURCE_STYLE = {
  RuleInput: 'tax_rule_style',
  RuleFields: 'tax_rule_style',
  RuleRate: 'tax_rate_style',
  PersonalInfo: 'tax_personal_style',
  Globals: 'tax_global_style',
  OutputVar: 'tax_output_style',
  Constant: 'tax_const_style',
  // A nested PATH. Its own hue for the same reason it has its own block: `[]` means "per item", and a
  // path that wore rule blue would read as one number when it is a whole column of them.
  Path: 'tax_path_style',
}
// The badge names WHERE the value comes from, in the words the admin uses for it. Both rule bridges say
// "Tax Rule" because both come from one; amount vs อัตรา is carried by the style, the gloss and the
// `_rate` suffix. DISPLAY-ONLY — the extraState codes (RuleInput/RuleRate/OutputVar/…) are what persist,
// and nothing keys off these strings.
const SOURCE_LABEL = {
  RuleInput: 'Tax Rule',
  // Not a rule — the off-catalog bucket DEFAULT_SOURCE coerces to (see setTaxFlagged's ⚠). Claiming
  // "Rule" here would tell the admin an unresolvable name resolved.
  RuleFields: '?',
  RuleRate: 'Tax Rule',
  PersonalInfo: 'Personal',
  Globals: 'Global',
  OutputVar: 'Formula',
  Constant: 'Const',
  // Not "Tax Rule": the ROOT is one, but what the block names is a FIELD inside it, possibly once per
  // item. Saying "Tax Rule" would claim the block is the rule's own single value.
  Path: 'ทุกรายการ',
}
const DEFAULT_SOURCE = 'RuleFields'

// ── The stale marker: "!" at the block's RIGHT end (single-row block → its top-right corner) ───────
// TWO INDICATORS, ONE SOURCE: setTaxFlagged writes both this label and Blockly's warning icon. The "!"
// says THAT the block is wrong at a glance across the canvas; the warning text says WHAT is wrong. A
// second path that derived either one independently could disagree with the badge — hence neither the
// view nor the toolbox touches the FLAG field.
const TAX_FLAG_FIELD = 'FLAG'
const TAX_FLAG_TEXT = '!'
const TAX_FLAG_CLASS = 'taxStaleFlag'
// Covers every deletable reference that lands on this path, not just the two the old wording named:
// a Tax Rule dropped from the config version, a ค่าคงที่ or a สูตร deleted from the calc version, or a
// personal/global name gone from the catalog.
const TAX_FLAG_WARNING = 'ชื่อนี้ไม่มีในเวอร์ชันนี้แล้ว — tax rule / ค่าคงที่ / สูตร / ตัวแปร ที่อ้างถึงถูกลบไป แก้สูตรให้ใช้ชื่อที่มีอยู่ ไม่งั้นการคำนวณจะผิดพลาด'

// Blockly renders block text INSIDE the injected SVG, so a Vue scoped <style> can never reach this
// field — the rule has to live in Blockly's own style blob (Css.register appends to it, and it is
// injected on Blockly.inject; registering after that throws, which import-time registration avoids).
//
// !important is REQUIRED, not defensive: the renderer injects `<injectionDiv> .blocklyText { fill: #fff }`
// (specificity 0,2,0) into its own <style>, so a plain class rule loses and the "!" renders white —
// invisible on the block, which is worse than not shipping it.
//
// The white halo (paint-order: stroke → the stroke is painted BEHIND the fill) is what makes one red
// work on every block fill: tax blocks are saturated mid-tones (rule blue #1976D2, cyan, purple …),
// and red-on-blue alone is a ~1.7:1 mush. Font SIZE is deliberately left alone — Blockly measures
// field width from its own constants, not from this CSS, so a bigger glyph would overflow its box.
const TAX_FLAG_CSS = `
.blocklyText.${TAX_FLAG_CLASS} {
  fill: #FF1744 !important;
  font-weight: 900 !important;
  stroke: #FFFFFF;
  stroke-width: 2.5px;
  paint-order: stroke;
}
`

// Doubles as the mutator bubble's heading, so the author can see which block they opened the gear from.
const VARIADIC_LABELS = { tax_func_min: 'MIN', tax_func_max: 'MAX' }

// ──────────────────────────────────────────────────────────────────────────────────
//  Custom clickable field for the Bracket block (opens the Vue table dialog).
//  The VIEW (task 2-D) supplies the handler via setBracketButtonHandler(); headless
//  never clicks, so this is import-safe with no DOM.
// ──────────────────────────────────────────────────────────────────────────────────
let bracketButtonHandler = null

/**
 * Register the callback the Bracket block's "⚙ แก้ตารางขั้นบันได" button invokes on click.
 * @param {?function(Object):void} fn  receives the clicked tax_bracket block (or null to clear)
 */
export function setBracketButtonHandler(fn) {
  bracketButtonHandler = typeof fn === 'function' ? fn : null
}

const BRACKET_BUTTON_TEXT = '⚙ แก้ตารางขั้นบันได'

class FieldBracketButton extends Blockly.FieldLabelSerializable {
  constructor(text, config) {
    super(text == null ? BRACKET_BUTTON_TEXT : text, undefined, config)
    // EDITABLE makes Blockly route clicks to showEditor_ (browser only). SERIALIZABLE (inherited
    // from FieldLabelSerializable) silences the "editable-but-not-serializable" warning; the
    // serialized label text is derived from the rows (re-set by loadExtraState) so it is harmless.
    this.EDITABLE = true
  }

  showEditor_() {
    if (!bracketButtonHandler) return
    try {
      bracketButtonHandler(this.getSourceBlock())
    } catch (_err) {
      /* view not mounted yet — ignore */
    }
  }
}
FieldBracketButton.fromJson = function fromJson(options) {
  return new FieldBracketButton(options && options.text)
}

// ──────────────────────────────────────────────────────────────────────────────────
//  tax_text — hold the TEXT field to the shared literal grammar as it is typed.
//  A plain extension (NOT a mutator): it adds no save/loadExtraState, only a validator.
// ──────────────────────────────────────────────────────────────────────────────────
/**
 * Blockly runs a field validator on every edit; returning a transformed string makes the
 * disallowed character simply never appear. Chosen over returning null (= silently reject the whole
 * edit, which reads as a frozen field) and over accepting anything (= the author only learns at save,
 * from a backend 400 that names a grammar they never saw).
 */
function taxTextValidatorExt() {
  const f = this.getField('TEXT')
  if (f && typeof f.setValidator === 'function') f.setValidator(sanitizeStringLiteral)
}

// ──────────────────────────────────────────────────────────────────────────────────
//  Mutator mixins (carry non-field data through JSON serialization).
//  Registered via Extensions.registerMutator (a plain extension may NOT add
//  saveExtraState/loadExtraState — Blockly.Extensions.apply rejects that).
// ──────────────────────────────────────────────────────────────────────────────────

// tax_define — appliesTo* / evalOrder / notes metadata (edited by the right-panel, D1).
const TAX_DEFINE_MUTATOR_MIXIN = {
  saveExtraState() {
    return {
      appliesToKind: this.taxAppliesToKind_ || 'RULE_CODE',
      appliesToKey: this.taxAppliesToKey_ || '',
      evalOrder: Number.isFinite(this.taxEvalOrder_) ? this.taxEvalOrder_ : 0,
      notes: this.taxNotes_ != null ? this.taxNotes_ : null,
    }
  },
  loadExtraState(state) {
    const s = state || {}
    this.taxAppliesToKind_ = s.appliesToKind || 'RULE_CODE'
    this.taxAppliesToKey_ = s.appliesToKey != null ? String(s.appliesToKey) : ''
    this.taxEvalOrder_ = Number.isFinite(Number(s.evalOrder)) ? Number(s.evalOrder) : 0
    this.taxNotes_ = s.notes != null ? s.notes : null
    this.updateAppliesLabel()
  },
  /**
   * Refresh the on-block badge that shows which rule / scope this define is bound to, so
   * rule-scoped defines (all using `input_value`) are distinguishable at a glance on the canvas.
   * DISPLAY-ONLY (FieldLabel, non-serializable — recomputed from meta on load).
   */
  updateAppliesLabel() {
    const f = this.getField('APPLIESLABEL')
    if (!f) return
    const kind = this.taxAppliesToKind_ || 'RULE_CODE'
    let txt
    if (kind === 'RULE_CODE') txt = this.taxAppliesToKey_ ? '◆ ' + this.taxAppliesToKey_ : '◆ (ยังไม่ผูก rule)'
    else txt = '◆ ' + kind
    try { f.setValue(txt) } catch (_e) { /* not rendered / headless — ignore */ }
  },
  /** @returns {{appliesToKind:string,appliesToKey:string,evalOrder:number,notes:?string,outputVar:string}} */
  getTaxMeta() {
    return {
      outputVar: this.getFieldValue('OUTPUTVAR') || '',
      appliesToKind: this.taxAppliesToKind_ || 'RULE_CODE',
      appliesToKey: this.taxAppliesToKey_ || '',
      evalOrder: Number.isFinite(this.taxEvalOrder_) ? this.taxEvalOrder_ : 0,
      notes: this.taxNotes_ != null ? this.taxNotes_ : null,
    }
  },
  /** @param {Object} meta partial metadata; only provided keys are applied */
  setTaxMeta(meta) {
    const m = meta || {}
    if (m.outputVar != null && this.getField('OUTPUTVAR')) this.setFieldValue(String(m.outputVar), 'OUTPUTVAR')
    if (m.appliesToKind != null) this.taxAppliesToKind_ = String(m.appliesToKind)
    if (m.appliesToKey != null) this.taxAppliesToKey_ = String(m.appliesToKey)
    if (m.evalOrder != null && Number.isFinite(Number(m.evalOrder))) this.taxEvalOrder_ = Number(m.evalOrder)
    if ('notes' in m) this.taxNotes_ = m.notes != null ? m.notes : null
    this.updateAppliesLabel()
  },
}
function taxDefineHelper() {
  if (this.taxAppliesToKind_ === undefined) {
    this.taxAppliesToKind_ = 'RULE_CODE'
    this.taxAppliesToKey_ = ''
    this.taxEvalOrder_ = 0
    this.taxNotes_ = null
  }
  if (typeof this.updateAppliesLabel === 'function') this.updateAppliesLabel()
}

// ── SHARED derived-annotation machinery (tax_variable + tax_path) ──────────────────────────────
// Both blocks name a catalog entry an admin can DELETE from the version — a variable name, or the
// rule a Σ walks — so both need the same two derived indicators. Extracted rather than copied: the
// whole reason the "!" and the ⚠ warning share one setter is that two derivations of one state is
// how they come to disagree, and a second copy of that setter would reintroduce exactly that.
const TAX_ANNOTATION_MIXIN = {
  /**
   * @param {boolean} flag off-catalog / orphaned reference → "!" + ⚠ warning.
   *
   * THE single writer of the flagged state: the FLAG label and the warning icon are two views of
   * `taxFlagged_`, never two derivations of it. The label is DERIVED like RULECTX (field_label →
   * non-serializable), so it stays out of the frozen saveExtraState shapes.
   */
  setTaxFlagged(flag) {
    this.taxFlagged_ = !!flag
    const f = this.getField(TAX_FLAG_FIELD)
    if (f) {
      try { f.setValue(this.taxFlagged_ ? TAX_FLAG_TEXT : '') } catch (_e) { /* headless / not rendered — ignore */ }
    }
    this.setWarningText(this.taxFlagged_ ? TAX_FLAG_WARNING : null)
  },
  isTaxFlagged() {
    return this.taxFlagged_ === true
  },
  /**
   * DISPLAY-ONLY annotation suffix — a short human gloss of what this block names
   * (e.g. "จำนวนเงิน · ค่าเช่าทรัพย์สิน"), so a bare identifier stops being the block's only text.
   * NEVER compiled and NEVER persisted (see each saveExtraState): the owning view re-derives it from
   * the live catalog on every load/change. Keep it SHORT — it widens the block; the full sentence
   * belongs in the tooltip.
   * @param {?string} text  empty/nullish clears the slot (an unresolved name gets NO gloss, never a guess)
   */
  setTaxDescription(text) {
    const f = this.getField('RULECTX')
    if (!f) return
    try { f.setValue(text == null ? '' : String(text)) } catch (_e) { /* headless / not rendered — ignore */ }
  },
}

// tax_variable — source badge + off-catalog flag + a DERIVED description (never persisted).
const TAX_VARIABLE_MUTATOR_MIXIN = {
  ...TAX_ANNOTATION_MIXIN,
  /**
   * MUST keep emitting exactly { source, flagged? }. `desc` is deliberately NOT saved: it is derived
   * display state, so a persisted copy would go stale the moment a rule or a สูตร is renamed, and
   * widening this object rewrites every stored ExpressionGraphJson — which both the AC4 zero-drift
   * contract and chooseFormulaSource's recompile check sit on top of.
   */
  saveExtraState() {
    const state = { source: this.taxSource_ || DEFAULT_SOURCE }
    if (this.taxFlagged_) state.flagged = true
    return state
  },
  /** `desc` is OPTIONAL and inbound-only (the toolbox seeds flyout blocks with it); absent → cleared. */
  loadExtraState(state) {
    const s = state || {}
    this.setTaxSource(s.source || DEFAULT_SOURCE)
    this.setTaxFlagged(s.flagged === true)
    this.setTaxDescription(s.desc)
  },
  /**
   * @param {string} source RuleInput|RuleRate|RuleFields|PersonalInfo|Globals|OutputVar|Constant.
   * Unknown → DEFAULT_SOURCE, which is also what classifyField hands back for an off-catalog name
   * (paired with flagged:true → the stale warning), so the coercion and the stale bucket agree.
   */
  setTaxSource(source) {
    this.taxSource_ = SOURCE_STYLE[source] ? source : DEFAULT_SOURCE
    const label = this.getField('SRC')
    if (label) label.setValue(SOURCE_LABEL[this.taxSource_] || 'Var')
    this.setStyle(SOURCE_STYLE[this.taxSource_] || 'tax_rule_style')
  },
  getTaxSource() {
    return this.taxSource_ || DEFAULT_SOURCE
  },
}
function taxVariableHelper() {
  if (this.taxSource_ === undefined) this.setTaxSource(DEFAULT_SOURCE)
  if (this.taxFlagged_ === undefined) this.taxFlagged_ = false
}

// ── tax_path — `INCOME_40_5.subItems[].items[].amount` ────────────────────────────────────────────
// The canonical path text is the ONLY thing that compiles, so it is what is PERSISTED (extraState
// `path`); everything the block SHOWS is derived from it on load — the `▸` spelling, the source badge,
// the gloss and the stale "!". That is the same division tax_variable already makes between `source`
// (stored) and `desc`/`flagged` (derived), reused via TAX_ANNOTATION_MIXIN rather than copied: two
// derivations of one state is how a block comes to disagree with the formula it compiles to.
//
// The path is NOT a field_input. A path must resolve against the tax form's real JSON shape, and a
// typeable box can only ever author one that does not — the same contract as tax_variable's NAME.
const TAX_PATH_MUTATOR_MIXIN = {
  ...TAX_ANNOTATION_MIXIN,
  saveExtraState() {
    const state = { path: this.taxPath_ || '', source: this.taxSource_ || 'Path' }
    if (this.taxFlagged_) state.flagged = true
    return state
  },
  /** `desc` is OPTIONAL and inbound-only (the toolbox seeds flyout blocks with it); absent → cleared. */
  loadExtraState(state) {
    const s = state || {}
    this.setTaxPath(s.path || '')
    this.setTaxSource(s.source || 'Path')
    this.setTaxFlagged(s.flagged === true)
    this.setTaxDescription(s.desc)
  },
  /**
   * @param {string} canonical  `a.b[].c` — the exact text that will be COMPILED.
   * Stored verbatim; the label shows `a ▸ b[] ▸ c`. A path that does not parse is still stored
   * verbatim (never silently corrected) — the generator refuses it loudly instead, which is the only
   * way the author finds out that what they are looking at is not what would run.
   */
  setTaxPath(canonical) {
    this.taxPath_ = String(canonical == null ? '' : canonical)
    const f = this.getField('PATH')
    if (f) {
      try { f.setValue(this.taxPath_ === '' ? 'path' : pathDisplay(this.taxPath_)) } catch (_e) { /* headless — ignore */ }
    }
  },
  getTaxPath() {
    return this.taxPath_ || ''
  },
  /** Same coercion rule as tax_variable's, so an unknown source lands in the same visible bucket. */
  setTaxSource(source) {
    this.taxSource_ = SOURCE_STYLE[source] ? source : 'Path'
    const label = this.getField('SRC')
    if (label) label.setValue(SOURCE_LABEL[this.taxSource_] || 'Path')
    this.setStyle(SOURCE_STYLE[this.taxSource_] || 'tax_path_style')
  },
  getTaxSource() {
    return this.taxSource_ || 'Path'
  },
}
function taxPathHelper() {
  if (this.taxPath_ === undefined) this.taxPath_ = ''
  if (this.taxSource_ === undefined) this.setTaxSource('Path')
  if (this.taxFlagged_ === undefined) this.taxFlagged_ = false
}

// ── tax_func_ifs — IFS(cond1, val1, …, TRUE, default), variadic in PAIRS ─────────────────────────
// Modelled on TAX_VARIADIC_MUTATOR_MIXIN (same decompose/compose/saveConnections dance, same ⊕/⊖
// icon) with ONE difference that is the whole point: the unit the mutator adds and removes is a
// (condition, value) PAIR, never a single socket. An odd argument count is not expressible from the
// editor at all, which is what makes `validateParsedArity`'s EVEN rule a check on hand-edited text
// rather than something an author can trip over.
const TAX_IFS_MUTATOR_MIXIN = {
  minPairs_: 1,
  saveExtraState() {
    return { pairCount: this.pairCount_ }
  },
  loadExtraState(state) {
    const n = state && Number(state.pairCount)
    this.pairCount_ = Math.max(this.minPairs_, Number.isFinite(n) ? n : this.minPairs_)
    this.updateShape_()
  },
  decompose(workspace) {
    const container = workspace.newBlock('tax_ifs_container')
    container.initSvg()
    let connection = container.getInput('STACK').connection
    for (let i = 0; i < this.pairCount_; i += 1) {
      const itemBlock = workspace.newBlock('tax_ifs_item')
      itemBlock.initSvg()
      connection.connect(itemBlock.previousConnection)
      connection = itemBlock.nextConnection
    }
    return container
  },
  compose(containerBlock) {
    let itemBlock = containerBlock.getInputTargetBlock('STACK')
    const conds = []
    const vals = []
    while (itemBlock) {
      if (itemBlock.isInsertionMarker()) { itemBlock = itemBlock.getNextBlock(); continue }
      conds.push(itemBlock.condConnection_)
      vals.push(itemBlock.valueConnection_)
      itemBlock = itemBlock.getNextBlock()
    }
    // Disconnect any child whose mutator item was removed — BOTH halves of the pair, or the surviving
    // half would silently pair up with a different condition.
    for (let i = 0; i < this.pairCount_; i += 1) {
      ;['COND' + i, 'VAL' + i].forEach((nm, half) => {
        const input = this.getInput(nm)
        const target = input && input.connection.targetConnection
        const keep = half === 0 ? conds : vals
        if (target && keep.indexOf(target) === -1) target.disconnect()
      })
    }
    this.pairCount_ = Math.max(this.minPairs_, conds.length)
    this.updateShape_()
    for (let i = 0; i < this.pairCount_; i += 1) {
      if (conds[i]) conds[i].reconnect(this, 'COND' + i)
      if (vals[i]) vals[i].reconnect(this, 'VAL' + i)
    }
  },
  saveConnections(containerBlock) {
    let itemBlock = containerBlock.getInputTargetBlock('STACK')
    let i = 0
    while (itemBlock) {
      if (itemBlock.isInsertionMarker()) { itemBlock = itemBlock.getNextBlock(); continue }
      const ci = this.getInput('COND' + i)
      const vi = this.getInput('VAL' + i)
      itemBlock.condConnection_ = ci && ci.connection.targetConnection
      itemBlock.valueConnection_ = vi && vi.connection.targetConnection
      i += 1
      itemBlock = itemBlock.getNextBlock()
    }
  },
  updateShape_() {
    if (!Number.isFinite(this.pairCount_) || this.pairCount_ < this.minPairs_) this.pairCount_ = this.minPairs_
    for (let i = 0; i < this.pairCount_; i += 1) {
      if (!this.getInput('COND' + i)) {
        this.appendValueInput('COND' + i).appendField(i === 0 ? 'IFS  ถ้า' : 'ถ้า')
      }
      if (!this.getInput('VAL' + i)) {
        this.appendValueInput('VAL' + i).setCheck(['Number', 'Boolean']).appendField('ให้ใช้')
      }
    }
    for (let i = this.pairCount_; this.getInput('COND' + i); i += 1) {
      this.removeInput('COND' + i)
      if (this.getInput('VAL' + i)) this.removeInput('VAL' + i)
    }
  },
}
function taxIfsHelper() {
  this.pairCount_ = this.minPairs_ || 1
  this.setInputsInline(false)
  this.updateShape_()
}

// tax_bracket — persist-only tier table.
function normalizeBracketRows(rows) {
  const list = Array.isArray(rows) ? rows : []
  return list
    .filter((r) => r != null)
    .map((r) => ({
      lowerBound: numOr(r.lowerBound, 0),
      upperBound: r.upperBound == null || r.upperBound === '' ? null : numOr(r.upperBound, null),
      rate: numOr(r.rate, 0),
      sortOrder: numOr(r.sortOrder, 0),
    }))
}
const TAX_BRACKET_MUTATOR_MIXIN = {
  saveExtraState() {
    return { rows: (this.taxRows_ || []).map((r) => ({ ...r })) }
  },
  loadExtraState(state) {
    this.taxSetRows((state && state.rows) || [])
  },
  /** @returns {Array<{lowerBound:number,upperBound:?number,rate:number,sortOrder:number}>} */
  getBracketRows() {
    return (this.taxRows_ || []).map((r) => ({ ...r }))
  },
  /** @param {Array} rows replace the tier rows + refresh the button label */
  taxSetRows(rows) {
    this.taxRows_ = normalizeBracketRows(rows)
    const btn = this.getField('EDIT')
    if (btn) {
      const n = this.taxRows_.length
      btn.setValue(n ? `${BRACKET_BUTTON_TEXT} (${n} ชั้น)` : BRACKET_BUTTON_TEXT)
    }
  },
}
function taxBracketHelper() {
  if (this.taxRows_ === undefined) this.taxRows_ = []
}

// tax_func_min / tax_func_max — variadic (Blockly built-in mutator with ⊕/⊖ icon).
const TAX_VARIADIC_MUTATOR_MIXIN = {
  minItems_: 2,
  saveExtraState() {
    return { itemCount: this.itemCount_ }
  },
  loadExtraState(state) {
    const n = state && Number(state.itemCount)
    this.itemCount_ = Math.max(this.minItems_, Number.isFinite(n) ? n : this.minItems_)
    this.updateShape_()
  },
  decompose(workspace) {
    const container = workspace.newBlock('tax_func_container')
    container.initSvg()
    // One container type serves both MIN and MAX, so the heading is set from whichever block was opened.
    const title = container.getField('TITLE')
    if (title && VARIADIC_LABELS[this.type]) {
      try { title.setValue(VARIADIC_LABELS[this.type]) } catch (_e) { /* headless — ignore */ }
    }
    let connection = container.getInput('STACK').connection
    for (let i = 0; i < this.itemCount_; i += 1) {
      const itemBlock = workspace.newBlock('tax_func_item')
      itemBlock.initSvg()
      connection.connect(itemBlock.previousConnection)
      connection = itemBlock.nextConnection
    }
    return container
  },
  compose(containerBlock) {
    let itemBlock = containerBlock.getInputTargetBlock('STACK')
    const connections = []
    while (itemBlock) {
      if (itemBlock.isInsertionMarker()) {
        itemBlock = itemBlock.getNextBlock()
        continue
      }
      connections.push(itemBlock.valueConnection_)
      itemBlock = itemBlock.getNextBlock()
    }
    // Disconnect any child whose mutator item was removed.
    for (let i = 0; i < this.itemCount_; i += 1) {
      const target = this.getInput('ADD' + i).connection.targetConnection
      if (target && connections.indexOf(target) === -1) target.disconnect()
    }
    this.itemCount_ = Math.max(this.minItems_, connections.length)
    this.updateShape_()
    for (let i = 0; i < this.itemCount_; i += 1) {
      if (connections[i]) connections[i].reconnect(this, 'ADD' + i)
    }
  },
  saveConnections(containerBlock) {
    let itemBlock = containerBlock.getInputTargetBlock('STACK')
    let i = 0
    while (itemBlock) {
      if (itemBlock.isInsertionMarker()) {
        itemBlock = itemBlock.getNextBlock()
        continue
      }
      const input = this.getInput('ADD' + i)
      itemBlock.valueConnection_ = input && input.connection.targetConnection
      i += 1
      itemBlock = itemBlock.getNextBlock()
    }
  },
  updateShape_() {
    if (!Number.isFinite(this.itemCount_) || this.itemCount_ < this.minItems_) this.itemCount_ = this.minItems_
    // Append missing value inputs (label the function name on the first).
    for (let i = 0; i < this.itemCount_; i += 1) {
      if (!this.getInput('ADD' + i)) {
        const input = this.appendValueInput('ADD' + i)
        if (i === 0) input.appendField(VARIADIC_LABELS[this.type] || 'MIN')
      }
    }
    // Remove surplus value inputs.
    for (let i = this.itemCount_; this.getInput('ADD' + i); i += 1) {
      this.removeInput('ADD' + i)
    }
  },
}
function taxVariadicHelper() {
  this.itemCount_ = this.minItems_ || 2
  this.setInputsInline(true)
  this.updateShape_()
}

// ──────────────────────────────────────────────────────────────────────────────────
//  Block JSON definitions
// ──────────────────────────────────────────────────────────────────────────────────
const MUTATOR_HELPER_BLOCKS_JSON = [
  {
    type: 'tax_func_container',
    // TITLE is set per parent by decompose — MIN or MAX, whichever was opened.
    message0: '%1 %2',
    args0: [
      { type: 'field_label', name: 'TITLE', text: VARIADIC_LABELS.tax_func_min },
      { type: 'input_statement', name: 'STACK' },
    ],
    style: 'tax_func_style',
    tooltip: 'ต่อ “ช่องใส่ค่า” กี่อัน บล็อกก็จะมีกี่ช่อง (อย่างน้อย 2 ช่อง)',
    enableContextMenu: false,
  },
  {
    type: 'tax_func_item',
    message0: 'ช่องใส่ค่า',
    previousStatement: null,
    nextStatement: null,
    style: 'tax_func_style',
    tooltip: 'ลากมาต่อในกล่อง = เพิ่มอีก 1 ช่อง · ลากออกจากกล่อง = เอาช่องนั้นออก',
    enableContextMenu: false,
  },
  {
    type: 'tax_ifs_container',
    message0: 'IFS %1',
    args0: [{ type: 'input_statement', name: 'STACK' }],
    style: 'tax_func_style',
    tooltip: 'ต่อ “เงื่อนไข → ค่า” กี่คู่ บล็อกก็จะมีกี่คู่ (อย่างน้อย 1 คู่)',
    enableContextMenu: false,
  },
  {
    // ONE item = ONE (condition, value) pair. The mutator cannot add half a pair, which is what makes
    // an odd argument count unreachable from the editor.
    type: 'tax_ifs_item',
    message0: 'เงื่อนไข → ค่า',
    previousStatement: null,
    nextStatement: null,
    style: 'tax_func_style',
    tooltip: 'ลากมาต่อในกล่อง = เพิ่มอีก 1 คู่ (เงื่อนไข + ค่า) · ลากออกจากกล่อง = เอาคู่นั้นออก',
    enableContextMenu: false,
  },
]

const TAX_BLOCKS_JSON = [
  {
    type: 'tax_start',
    message0: '▶ เริ่มคำนวณภาษี — START',
    nextStatement: null,
    inputsInline: true,
    style: 'tax_start_style',
    tooltip: 'จุดเริ่มต้นการคำนวณ — ต่อบล็อกนิยามสูตร (define) เรียงจากบนลงล่างตามลำดับการคำนวณ แล้วปิดท้ายด้วยบล็อก FINAL',
    helpUrl: '',
  },
  {
    type: 'tax_define',
    message0: 'นิยาม (define) %1 %2 = %3',
    args0: [
      { type: 'field_input', name: 'OUTPUTVAR', text: 'output_var' },
      { type: 'field_label', name: 'APPLIESLABEL', text: '' },
      { type: 'input_value', name: 'EXPR' },
    ],
    inputsInline: true,
    previousStatement: null,
    nextStatement: null,
    style: 'tax_output_style',
    tooltip: 'นิยามสูตรหนึ่งชุด: กำหนดชื่อผลลัพธ์ (OutputVar) แล้วต่อบล็อกสูตรลงในช่อง — ต่อในสายใต้ START ตามลำดับ',
    helpUrl: '',
    mutator: 'tax_define_mutator',
  },
  {
    type: 'tax_result',
    message0: '🏁 ผลลัพธ์สุดท้าย — FINAL: เงินได้สุทธิ = %1',
    args0: [
      { type: 'field_input', name: 'RESULTVAR', text: 'net_income' },
    ],
    previousStatement: null,
    inputsInline: true,
    style: 'tax_result_style',
    tooltip: 'ปิดท้ายการคำนวณ — ระบุตัวแปรผลลัพธ์ที่เครื่องยนต์จะนำไปเดินขั้นบันไดภาษี (ปกติคือ net_income)',
    helpUrl: '',
  },
  {
    // The Formula editor's compile anchor. NO output / previous / next → a true root that can never be
    // swallowed into someone else's socket. Only what is plugged into VALUE compiles; the rest of the
    // canvas is the author's scratch space and is left alone.
    type: TAX_FORMULA_RESULT_TYPE,
    message0: '🏁 ผลลัพธ์ของสูตรนี้ = %1',
    args0: [{ type: 'input_value', name: 'VALUE' }],
    inputsInline: true,
    style: 'tax_result_style',
    tooltip: 'จุดจับผลลัพธ์: เฉพาะบล็อกที่เสียบในช่องนี้เท่านั้นที่ถูกคำนวณและบันทึกเป็นสูตร — บล็อกอื่นที่วางลอยไว้บนแคนวาสเก็บไว้ได้ ไม่ถูกคำนวณ',
    helpUrl: '',
  },
  {
    type: 'tax_variable',
    message0: '%1 %2 %3 %4',
    args0: [
      { type: 'field_label', name: 'SRC', text: SOURCE_LABEL[DEFAULT_SOURCE] },
      // FIXED text, never an input box: a block IS its identifier. field_label_serializable is
      // SERIALIZABLE=true + EDITABLE=false → same `fields: { NAME }` blob as the field_input it
      // replaced (old graphs load unchanged), but the name can only be set by code — the flyout,
      // or ncalcToBlocks rebuilding a tree — never typed.
      { type: 'field_label_serializable', name: 'NAME', text: 'variable' },
      // Description slot — display-only, NOT serializable (that is why `desc` travels in extraState
      // rather than as a `fields:` entry a flyout could prefill).
      { type: 'field_label', name: 'RULECTX', text: '' },
      // Stale marker, LAST → the right end of this single-row block = the top-right corner. Empty by
      // default; only setTaxFlagged writes it. field_label (not field_label_serializable) is what keeps
      // it out of the stored blob — it is derived from `flagged`, exactly like RULECTX is from `desc`.
      // `class` is honoured by FieldLabel.fromJson → configure_ → initView adds it to the <text>.
      { type: 'field_label', name: TAX_FLAG_FIELD, text: '', class: TAX_FLAG_CLASS },
    ],
    output: 'Number',
    style: 'tax_rule_style',
    tooltip: 'ตัวแปร: อ้างชื่อ rule input / rule field / ข้อมูลส่วนตัว / global / ผลลัพธ์สูตรอื่น — แก้ชื่อในบล็อกไม่ได้ ถ้าต้องการตัวแปรอื่นให้ลากบล็อกใหม่จากกล่องเครื่องมือ',
    helpUrl: '',
    mutator: 'tax_variable_mutator',
  },
  {
    // A NESTED path into the tax form's JSON: `INCOME_40_5.subItems[].items[].amount`.
    // `[]` = "for every item at this position". Two paths sharing a `[]`-prefix walk the SAME item
    // together (never a cross product), and the whole formula's per-iteration values are SUMMED —
    // which is what makes `amount * IFS(subTypeCode = …)` the §40(5) expense and not a random product.
    type: 'tax_path',
    message0: '%1 %2 %3 %4',
    args0: [
      { type: 'field_label', name: 'SRC', text: SOURCE_LABEL.Path },
      // DERIVED display text (`a ▸ b[] ▸ c`), NOT the stored value: the canonical path lives in
      // extraState.path, because that is the string that compiles and it must survive verbatim.
      { type: 'field_label', name: 'PATH', text: 'path' },
      { type: 'field_label', name: 'RULECTX', text: '' },
      { type: 'field_label', name: TAX_FLAG_FIELD, text: '', class: TAX_FLAG_CLASS },
    ],
    output: 'Number',
    style: 'tax_path_style',
    tooltip:
      'เส้นทางข้อมูลแบบซ้อน เช่น INCOME_40_5 ▸ subItems[] ▸ items[] ▸ amount. '
      + '[] หมายถึง “ทุกๆ รายการในชั้นนั้น” — เส้นทางที่ขึ้นต้นเหมือนกันจะวนรายการเดียวกันพร้อมกัน (ไม่ใช่จับคู่ข้ามกัน) '
      + 'และผลลัพธ์ของทุกรอบจะถูกบวกรวมกันเป็นค่าเดียว. แก้ชื่อในบล็อกไม่ได้ — ถ้าต้องการเส้นทางอื่นให้ลากบล็อกใหม่จากกล่องเครื่องมือ',
    helpUrl: '',
    mutator: 'tax_path_mutator',
  },
  {
    type: 'tax_number',
    message0: '%1',
    args0: [{ type: 'field_number', name: 'VALUE', value: 0 }],
    output: 'Number',
    style: 'tax_number_style',
    tooltip: 'ค่าตัวเลขคงที่ (numeric literal)',
    helpUrl: '',
  },
  {
    // A typed literal. `field_input` is deliberate and is the ONLY hand-typed identifier-ish text in
    // the palette: unlike a variable NAME (which must resolve in the catalog, so it can only come from
    // the flyout), a literal is the author's own value — no list could offer every 'FEMALE' / 'หญิง' / '90'.
    type: 'tax_text',
    message0: '“%1”', // the quotes are the block's chrome: they say "text" without spending a label on it
    args0: [{ type: 'field_input', name: 'TEXT', text: 'FEMALE' }],
    inputsInline: true,
    // "String" is what keeps the literal out of arithmetic (tax_arith checks Number|Boolean) while the
    // unchecked comparison sockets still take it.
    output: 'String',
    style: 'tax_text_style',
    tooltip:
      'ข้อความคงที่ (text) — ใช้เทียบค่ากับตัวแปรที่เก็บเป็นข้อความ เช่น sex == “FEMALE”. '
      + 'พิมพ์ให้ตรงกับค่าที่ระบบเก็บไว้ (ดูคำอธิบายของตัวแปร เช่น sex = MALE | FEMALE). '
      + 'ใส่ได้เฉพาะตัวอักษร ตัวเลข เว้นวรรค และ _ - . / ไม่เกิน ' + MAX_STRING_LITERAL_LEN + ' ตัวอักษร. '
      + 'เสียบได้เฉพาะช่องเปรียบเทียบ — ต่อเข้าบล็อกคำนวณ (+ − × ÷) ไม่ได้ และเป็นผลลัพธ์ของสูตรไม่ได้ (สูตรต้องคืนค่าเป็นตัวเลข)',
    helpUrl: '',
    extensions: ['tax_text_validate'],
  },
  {
    type: 'tax_arith',
    message0: '%1 %2 %3',
    args0: [
      // check keeps the tax_text literal OUT of arithmetic — that is the whole job of this check.
      // Boolean stays allowed because the frozen corpus already compiles `x * (a >= b)` (AC4): narrowing
      // to Number alone would silently drop that subtree on load, which is a regression, not a fix.
      { type: 'input_value', name: 'A', check: ['Number', 'Boolean'] },
      { type: 'field_dropdown', name: 'OP', options: OP_OPTIONS },
      { type: 'input_value', name: 'B', check: ['Number', 'Boolean'] },
    ],
    inputsInline: true,
    output: 'Number',
    style: 'tax_op_style',
    tooltip: 'ตัวดำเนินการเลขคณิต: + − × ÷ (ใส่ได้เฉพาะตัวเลข — บล็อกข้อความใช้ที่นี่ไม่ได้)',
    helpUrl: '',
  },
  {
    type: 'tax_compare',
    message0: '%1 %2 %3',
    args0: [
      // UNCHECKED on purpose: an absent check already accepts every output type, tax_text's "String"
      // included, so the text literal plugs in with ZERO change here. Spelling out ['Number','String']
      // would NARROW this socket (a nested comparison loads today) — the opposite of the ask.
      { type: 'input_value', name: 'A' },
      { type: 'field_dropdown', name: 'OP', options: CMP_OPTIONS },
      { type: 'input_value', name: 'B' },
    ],
    inputsInline: true,
    output: 'Boolean',
    style: 'tax_compare_style',
    tooltip: 'การเปรียบเทียบ: < <= > >= == != (เทียบตัวเลขหรือข้อความก็ได้ — ข้อความใช้ == / != เป็นหลัก)',
    helpUrl: '',
  },
  {
    type: 'tax_func_min',
    output: 'Number',
    inputsInline: true,
    style: 'tax_func_style',
    tooltip: 'เอาค่าที่น้อยที่สุดจากทุกช่อง · กดฟันเฟืองเพื่อเพิ่ม/ลดช่อง (อย่างน้อย 2 ช่อง)',
    helpUrl: '',
    mutator: 'tax_variadic_mutator',
  },
  {
    type: 'tax_func_max',
    output: 'Number',
    inputsInline: true,
    style: 'tax_func_style',
    tooltip: 'เอาค่าที่มากที่สุดจากทุกช่อง · กดฟันเฟืองเพื่อเพิ่ม/ลดช่อง (อย่างน้อย 2 ช่อง)',
    helpUrl: '',
    mutator: 'tax_variadic_mutator',
  },
  {
    type: 'tax_func_round',
    message0: 'ROUND %1 ทศนิยม %2',
    args0: [
      { type: 'input_value', name: 'X' },
      { type: 'input_value', name: 'N' },
    ],
    inputsInline: true,
    output: 'Number',
    style: 'tax_func_style',
    tooltip: 'ROUND(x) หรือ ROUND(x, n) — ปัดเศษ (ช่องทศนิยม N เว้นว่าง = ปัดเป็นจำนวนเต็ม)',
    helpUrl: '',
  },
  {
    type: 'tax_func_if',
    message0: 'ถ้า (IF) %1 แล้ว %2 มิฉะนั้น %3',
    args0: [
      { type: 'input_value', name: 'COND' },
      { type: 'input_value', name: 'THEN' },
      { type: 'input_value', name: 'ELSE' },
    ],
    inputsInline: false,
    output: 'Number',
    style: 'tax_func_style',
    tooltip: 'IF(cond, then, else) — เงื่อนไข',
    helpUrl: '',
  },
  {
    // IFS(cond1, val1, cond2, val2, …, TRUE, default) — first TRUE condition wins.
    // Inputs come in PAIRS (COND0/VAL0, COND1/VAL1, …); the mutator adds and removes a whole pair.
    type: 'tax_func_ifs',
    output: 'Number',
    inputsInline: false,
    style: 'tax_func_style',
    tooltip:
      'IFS(เงื่อนไข1, ค่า1, เงื่อนไข2, ค่า2, …) — ใช้ค่าของเงื่อนไขแรกที่เป็นจริง. '
      + 'ปิดท้ายด้วยคู่ TRUE → ค่าเริ่มต้น เพื่อให้มีคำตอบเสมอ. กดฟันเฟืองเพื่อเพิ่ม/ลดคู่เงื่อนไข',
    helpUrl: '',
    mutator: 'tax_ifs_mutator',
  },
  {
    // Σ SUM(<นิพจน์ต่อรายการ>) — the EXPLICIT boundary of an iterated computation.
    //
    // A `[]` path already iterates and its per-iteration values are already summed. This block exists
    // for the ONE question that has two ordinary answers: does a function apply per row, or to the
    // total? `MIN(path * 0.5, 100000)` with no Σ caps EVERY ROW at 100000 and adds the caps up — a
    // number that looks right and is not the §40(1) deduction. So when a function wraps the whole
    // iterated computation the editor refuses it and asks for one of the two spellings, which is what
    // this block is: `MIN(Σ(path * 0.5), 100000)` or `Σ(MIN(path * 0.5, 100000))`.
    type: 'tax_func_sum',
    message0: 'Σ รวมทุกรายการของ %1',
    args0: [{ type: 'input_value', name: 'VALUE', check: ['Number', 'Boolean'] }],
    inputsInline: true,
    output: 'Number',
    style: 'tax_item_style',
    tooltip:
      'รวมทุกรายการ (Σ) — คำนวณสูตรข้างในทีละรายการตามเส้นทางข้อมูลที่มี [] แล้วบวกผลลัพธ์ทุกรายการเข้าด้วยกันเป็นค่าเดียว. '
      + 'ใช้เมื่อมีฟังก์ชัน (MIN / MAX / ROUND / IF / IFS) ครอบการวนรายการอยู่ เพื่อบอกว่าจะรวมก่อนหรือหลังฟังก์ชันนั้น '
      + 'เช่น MIN(Σ(ยอดต่อรายการ), 100000) = จำกัดยอดรวม ส่วน Σ(MIN(ยอดต่อรายการ, 100000)) = จำกัดทีละรายการ. '
      + 'ข้างใน Σ ต้องมีเส้นทางข้อมูลที่มี [] อย่างน้อยหนึ่งเส้น และซ้อน Σ ใน Σ ไม่ได้',
    helpUrl: '',
  },
  {
    // TRUE / FALSE. It exists for IFS's always-matching last branch — `IFS(…, TRUE, 0)` is how an
    // author writes "otherwise" — and it is a Boolean, so it plugs into a condition socket and not
    // into arithmetic.
    type: 'tax_bool',
    message0: '%1',
    args0: [{
      type: 'field_dropdown',
      name: 'VALUE',
      options: [['TRUE (เสมอ)', 'TRUE'], ['FALSE (ไม่เคย)', 'FALSE']],
    }],
    output: 'Boolean',
    style: 'tax_bool_style',
    tooltip: 'ค่าความจริงคงที่ — ใช้เป็นเงื่อนไขสุดท้ายของ IFS เพื่อกำหนดค่าเริ่มต้น (IFS(…, TRUE, 0))',
    helpUrl: '',
  },
  {
    type: 'tax_constant',
    message0: 'ค่าคงที่ %1 = %2',
    args0: [
      { type: 'field_input', name: 'CONSTKEY', text: 'CONST_KEY' },
      { type: 'field_number', name: 'CONSTVALUE', value: 0 },
    ],
    inputsInline: true,
    output: 'Number',
    style: 'tax_const_style',
    tooltip: 'ค่าคงที่: คอมไพล์เป็นชื่อ (ConstKey); ค่าถูก persist แยก (ไม่ถูก inline ในสูตร)',
    helpUrl: '',
  },
  {
    type: 'tax_bracket',
    message0: '▦ ขั้นบันได (Bracket) %1',
    args0: [{ type: 'field_bracket_button', name: 'EDIT', text: BRACKET_BUTTON_TEXT }],
    style: 'tax_bracket_style',
    tooltip: 'ตารางขั้นบันได — ไม่คอมไพล์ (engine เดิน bracket-walk เอง); เก็บชั้นภาษีเพื่อ persist',
    helpUrl: '',
    mutator: 'tax_bracket_mutator',
  },
]

// ──────────────────────────────────────────────────────────────────────────────────
//  Idempotent registration
// ──────────────────────────────────────────────────────────────────────────────────
let _registered = false

/**
 * Register every custom field / mutator / block definition. Safe to call repeatedly and from
 * both the view and the headless tests — guarded against double-registration.
 * @param {Object} [BlocklyArg] optional Blockly instance (defaults to the shim's singleton)
 */
export function registerTaxBlocks(BlocklyArg) {
  const B = BlocklyArg || Blockly
  if (_registered) return
  _registered = true

  // 1) custom field for the Bracket button (guarded — registry throws on a duplicate type).
  try {
    B.fieldRegistry.register('field_bracket_button', FieldBracketButton)
  } catch (_err) {
    /* already registered by a prior module instance — ignore */
  }

  // 2a) plain extension: the tax_text TEXT field validator (adds no extraState → not a mutator).
  registerExtensionOnce(B, 'tax_text_validate', taxTextValidatorExt)

  // 2) mutators (each carries saveExtraState/loadExtraState — must be a mutator, not a plain ext).
  registerMutatorOnce(B, 'tax_define_mutator', TAX_DEFINE_MUTATOR_MIXIN, taxDefineHelper)
  registerMutatorOnce(B, 'tax_variable_mutator', TAX_VARIABLE_MUTATOR_MIXIN, taxVariableHelper)
  registerMutatorOnce(B, 'tax_bracket_mutator', TAX_BRACKET_MUTATOR_MIXIN, taxBracketHelper)
  registerMutatorOnce(B, 'tax_variadic_mutator', TAX_VARIADIC_MUTATOR_MIXIN, taxVariadicHelper, ['tax_func_item'])
  registerMutatorOnce(B, 'tax_path_mutator', TAX_PATH_MUTATOR_MIXIN, taxPathHelper)
  registerMutatorOnce(B, 'tax_ifs_mutator', TAX_IFS_MUTATOR_MIXIN, taxIfsHelper, ['tax_ifs_item'])

  // 3) block definitions (mutator helper blocks first, then the primary blocks).
  const defineBlocks = B.defineBlocksWithJsonArray || (B.common && B.common.defineBlocksWithJsonArray)
  defineBlocks(MUTATOR_HELPER_BLOCKS_JSON)
  defineBlocks(TAX_BLOCKS_JSON)

  // 4) the "!" style, into Blockly's own style blob. Runs at IMPORT time (this function is called on
  // module load), which is what puts it before every Blockly.inject — Css.register throws once the
  // blob is injected. DOM-free (only Css.inject touches document), so headless imports are safe.
  registerTaxCssOnce(B)
}

function registerTaxCssOnce(B) {
  if (!B.Css || typeof B.Css.register !== 'function') return
  try {
    B.Css.register(TAX_FLAG_CSS)
  } catch (_err) {
    /* blob already injected (a re-import after a workspace was created) — the rule is already in it */
  }
}

function registerExtensionOnce(B, name, fn) {
  if (B.Extensions.isRegistered && B.Extensions.isRegistered(name)) return
  try {
    B.Extensions.register(name, fn)
  } catch (_err) {
    /* already registered by a prior module instance — ignore */
  }
}

function registerMutatorOnce(B, name, mixin, helper, blockList) {
  if (B.Extensions.isRegistered && B.Extensions.isRegistered(name)) return
  try {
    B.Extensions.registerMutator(name, mixin, helper, blockList)
  } catch (_err) {
    /* already registered — ignore */
  }
}

function numOr(value, fallback) {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

// Register on import so headless tests can `ws.newBlock(type)` immediately.
registerTaxBlocks(Blockly)

export default {
  TAX_BLOCK_TYPES,
  TAX_BLOCK_STYLE_KEYS,
  TAX_MUTATOR_BLOCK_TYPES,
  TAX_FORMULA_RESULT_TYPE,
  registerTaxBlocks,
  setBracketButtonHandler,
}
